import "./DailyBriefing.scss";
import React from "react";
import {observer} from "mobx-react-lite";
import {Button, ButtonBase, Chip, IconButton, Popover, Tooltip} from "@mui/material";
import {
    AddCircleOutline,
    ArrowBack,
    ArrowForward,
    CheckCircleOutline,
    Close,
    ControlPointDuplicateOutlined,
    DescriptionOutlined,
    EditOutlined,
    GridViewOutlined,
    InfoOutlined,
    LayersOutlined,
    RadioButtonCheckedOutlined,
    ScheduleOutlined,
    TaskAltOutlined,
    TrackChangesOutlined,
    TrendingUp,
} from "@mui/icons-material";
import {generatePath, useLocation, useNavigate, useParams} from "react-router";
import {MainPageFacade} from "../../components/main/page/MainPageFacade.tsx";
import {routes} from "../../routing/routes.ts";
import {BRIEFING, CONFIDENCE_DIMENSIONS} from "./briefingData";
import type {ComputedOpportunity} from "./briefingLogic";
import {
    confidenceDisplay,
    confidenceState,
    costOfWaitingTag,
    dimensionLabel,
    findAssumption,
    FIX_OPPORTUNITY_ID,
    FIX_DELTA,
    formatAssumptionValue,
    formatCompactEuro,
    readyToActLabel,
    weakestDimension,
} from "./briefingLogic";
import type {Assumption, ConfidenceDimension, Fact, WhyNowRef} from "./briefingTypes";
import {BriefingProvider, useBriefing} from "./briefingState";
import {AssumptionEditor} from "./components/AssumptionEditor.tsx";
import {DismissDialog} from "./components/DismissDialog.tsx";
import {DimensionSection, FactRow, sourceLabels, WorklistOffer} from "./components/ConfidenceDimensions.tsx";
import {OpportunityTypeIcon} from "./opportunityTypeIcon.tsx";

const fmtFullEuro = (v: number) => `€${Math.round(v).toLocaleString('en-GB')}`;

type SourceAnchor = {cls: ConfidenceDimension, item: Fact, el: HTMLElement};

/**
 * The full opportunity view — an audit-grade workspace. Narrative is a
 * rendering, never editable itself: every why-now line and every impact row
 * carries its SOURCE, and the source is the handle (assumptions edit with a
 * reason, observed records drill through read-only, owner-routed items ask the
 * owner). Fix routes return to this page.
 */
const OpportunityDetailContent: React.FC = observer(() => {
    const {state, view, dispatch} = useBriefing();
    const {opportunityId} = useParams<{opportunityId: string}>();
    const navigate = useNavigate();
    const location = useLocation();

    const [tab, setTab] = React.useState<'overview' | 'confidence' | 'actions'>('overview');
    const [editingAssumption, setEditingAssumption] = React.useState<string | null>(null);
    const [sourceAnchor, setSourceAnchor] = React.useState<SourceAnchor | null>(null);
    const [dimensionAnchor, setDimensionAnchor] = React.useState<{cls: ConfidenceDimension, el: HTMLElement} | null>(null);
    const [dismissOpen, setDismissOpen] = React.useState(false);
    const timingCardRef = React.useRef<HTMLDivElement>(null);

    const briefingPath = location.pathname.replace(/\/opportunity\/[^/]*$/, '');
    const returnPath = location.pathname;

    const computed: ComputedOpportunity | undefined =
        [...view.readyToAct, ...view.needsConfirmation, ...view.createdInitiatives].find(c => c.opportunity.id === opportunityId);

    React.useEffect(() => {
        if (!computed) navigate(briefingPath, {replace: true});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [computed === undefined]);
    if (!computed) return null;

    const opportunity = computed.opportunity;
    const detail = opportunity.detail_view;
    const vc = opportunity.potential_impact;
    const threshold = BRIEFING.ready_to_act_gate.confidence_threshold;
    const valueType = BRIEFING.impact_discipline.types.find(t => t.id === computed.impactType);
    const isConverted = state.createdInitiatives.includes(opportunity.id);
    const isGated = view.needsConfirmation.some(c => c.opportunity.id === opportunity.id);
    const rank = view.readyToAct.findIndex(c => c.opportunity.id === opportunity.id) + 1;
    const itemCount = CONFIDENCE_DIMENSIONS.reduce((acc, cls) => acc + opportunity.confidence[cls].facts.length, 0);
    // What requires attention: the dimension that sets confidence, plus its costliest gap.
    const weakest = weakestDimension(computed);
    const weakestGap = [...computed.checklists[weakest]]
        .filter(c => c.deduction > 0)
        .sort((a, b) => b.deduction - a.deduction)[0];
    const editingAssumptionDef: Assumption | undefined =
        editingAssumption ? findAssumption(editingAssumption)?.assumption : undefined;

    // ---- Value logic rows (the chain reconciles to the displayed value) ----
    const baseSpend = state.fixApplied && opportunity.id === FIX_OPPORTUNITY_ID ? FIX_DELTA.baseSpend : vc.base_spend;
    const effectiveRatePct = computed.impact / (baseSpend * vc.addressable_share * vc.continuation_factor) * 100;
    const rateAssumptionId = ['opportunity_rate', 'target_terms', 'scope_adjustment']
        .find(id => opportunity.assumptions?.some(a => a.id === id));
    const noteFirst = vc.note?.split('. ')[0];
    const valueRows: Array<{label: string, value: string, cls: ConfidenceDimension, sub?: string, assumptionId?: string}> = [
        {label: 'Base spend', value: fmtFullEuro(baseSpend), cls: 'scope'},
        {
            label: 'Addressable share', value: `× ${vc.addressable_share.toFixed(2)}`, cls: 'current_activity',
            sub: noteFirst?.startsWith('Addressable') ? noteFirst : undefined,
        },
        {
            label: 'Continuation factor', value: `× ${vc.continuation_factor}`, cls: 'future_conditions',
            sub: noteFirst?.startsWith('Continuation') ? noteFirst : undefined,
        },
        {
            label: 'Rate gap', value: `× ${parseFloat(effectiveRatePct.toFixed(2))}%`, cls: 'commercial_basis',
            assumptionId: rateAssumptionId,
            sub: opportunity.impact_logic?.benchmark_basis ?? opportunity.impact_logic?.explicit_note
                ?? (noteFirst && !noteFirst.startsWith('Addressable') && !noteFirst.startsWith('Continuation') ? noteFirst : undefined),
        },
    ];

    // ---- Why-now source affordances: the prose renders, the source edits ----
    const renderWhyNowRef = (ref: WhyNowRef | undefined, key: number) => {
        if (!ref) return null;
        switch (ref.type) {
            case 'fact': {
                const item = opportunity.confidence[ref.dimension].facts.find(i => i.record_id === ref.record);
                if (!item) return null;
                return (
                    <Tooltip key={key} title={`Source: ${item.record_id} · ${sourceLabels(item.source_system)}`}>
                        <IconButton
                            size="small"
                            className="db-op-src"
                            aria-label={`Source ${item.record_id}`}
                            onClick={e => setSourceAnchor({cls: ref.dimension, item, el: e.currentTarget})}
                        >
                            <DescriptionOutlined/>
                        </IconButton>
                    </Tooltip>
                );
            }
            case 'assumption': {
                const assumption = findAssumption(ref.id)?.assumption;
                if (!assumption) return null;
                return (
                    <Tooltip key={key} title={`Assumption: ${assumption.label} — override requires a reason`}>
                        <IconButton size="small" className="db-op-src" aria-label={`Edit assumption ${assumption.label}`}
                                    onClick={() => setEditingAssumption(ref.id)}>
                            <EditOutlined/>
                        </IconButton>
                    </Tooltip>
                );
            }
            case 'derivation':
                return (
                    <Tooltip key={key} title="Source: the timing derivation">
                        <IconButton size="small" className="db-op-src" aria-label="Open timing derivation"
                                    onClick={() => timingCardRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'})}>
                            <ScheduleOutlined/>
                        </IconButton>
                    </Tooltip>
                );
            case 'opportunity': {
                const target = BRIEFING.opportunities.find(c => c.id === ref.id);
                return (
                    <Tooltip key={key} title={`Tracked on: ${target?.subject ?? ref.id}`}>
                        <IconButton size="small" className="db-op-src" aria-label="Open related opportunity"
                                    onClick={() => navigate(`${briefingPath}/move/${ref.id}`)}>
                            <ArrowForward/>
                        </IconButton>
                    </Tooltip>
                );
            }
        }
    };

    const breadcrumb = (
        <span className="db-op-crumb">
            <ButtonBase className="db-op-back" onClick={() => navigate(briefingPath)}>
                <ArrowBack sx={{fontSize: 14}}/> Daily briefs
            </ButtonBase>
            <span className="db-op-crumb-meta">
                {rank > 0 ? `${rank} of ${view.readyToAct.length}` : isConverted ? 'tracked initiative' : 'needs confirmation'} · ranked by cost of waiting
            </span>
        </span>
    );

    const stripChips: Array<{icon: React.ReactNode, label: string, value: string, qualifier: string, onClick?: () => void}> = [
        ...computed.facts.filter(f => f.type !== 'timing').map(f => ({
            icon: <AddCircleOutline/>, label: f.label, value: f.display,
            qualifier: valueType?.unit ?? '',
        })),
        ...computed.facts.filter(f => f.type === 'timing').map(f => ({
            icon: <ScheduleOutlined/>, label: f.label, value: f.display,
            qualifier: opportunity.timing.type === 'deadline' ? 'before last responsible start' : 'while it stays open',
            onClick: () => timingCardRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'}),
        })),
        {icon: <TrendingUp/>, label: 'Cost of waiting', value: computed.costDisplay, qualifier: costOfWaitingTag(opportunity)},
        {
            icon: <TaskAltOutlined/>, label: 'Confidence', value: confidenceDisplay(computed),
            qualifier: readyToActLabel(computed), onClick: () => setTab('confidence'),
        },
        // Base spend is deliberately absent: the applies-to line above states it,
        // and the potential-impact chain below opens with it.
    ];

    return <MainPageFacade title={breadcrumb} className="db-opportunity-page">
        {state.fixApplied && state.bannerVisible &&
            <div className="db-diff-banner" role="status">
                <span>{BRIEFING.human_in_the_loop.diff_banner_example}</span>
                <IconButton size="small" aria-label="Dismiss update" onClick={() => dispatch({type: 'dismissBanner'})}>
                    <Close fontSize="small"/>
                </IconButton>
            </div>
        }
        {state.assumptionBanner &&
            <div className="db-diff-banner" role="status">
                <span>{state.assumptionBanner}</span>
                <IconButton size="small" aria-label="Dismiss update" onClick={() => dispatch({type: 'clearAssumptionBanner'})}>
                    <Close fontSize="small"/>
                </IconButton>
            </div>
        }

        <div className="db-op-idrow">
            <Tooltip title={opportunity.opportunity_type_label}>
                <span className="db-op-typemark">
                    <OpportunityTypeIcon moveType={opportunity.opportunity_type}/>
                </span>
            </Tooltip>
            <Tooltip title="Open in Supplier 360">
                <ButtonBase
                    className="db-op-supplier"
                    onClick={() => navigate(generatePath(routes.supplier_360, {id: location.pathname.split('/')[2]}))}
                >
                    <LayersOutlined sx={{fontSize: 13}}/> {opportunity.subject}
                </ButtonBase>
            </Tooltip>
            <span className={`db-card-pill db-tone-${opportunity.status_pill.tone}`}>{opportunity.status_pill.label}</span>
            <span className="db-op-slug">{opportunity.id}</span>
        </div>

        <h1 className="db-op-headline">{opportunity.title}</h1>
        <div className="db-op-meta">
            {opportunity.opportunity_type_label} · Applies to {computed.appliesTo}
        </div>

        <div className="db-op-strip">
            {stripChips.map((chip, i) =>
                <ButtonBase key={i} className="db-op-chip" disabled={!chip.onClick} onClick={chip.onClick}>
                    {chip.icon}
                    <span className="db-op-chip-label">{chip.label}:</span>
                    <b>{chip.value}</b>
                    <span className="db-op-chip-qualifier">· {chip.qualifier}</span>
                </ButtonBase>
            )}
        </div>

        {isGated &&
            <div className="db-op-gated">
                Needs confirmation — {dimensionLabel(weakest)} is {confidenceState(computed.dimensionConfidences[weakest]).toLowerCase()} at {computed.confidence}%, under the {threshold} gate. Not ready to act until it recovers.
            </div>
        }

        <div className="db-op-tabs" role="tablist">
            <ButtonBase className={"db-op-tab" + (tab === 'overview' ? ' active' : '')} role="tab"
                        aria-selected={tab === 'overview'} onClick={() => setTab('overview')}>
                <RadioButtonCheckedOutlined/> Overview
            </ButtonBase>
            <ButtonBase className={"db-op-tab" + (tab === 'confidence' ? ' active' : '')} role="tab"
                        aria-selected={tab === 'confidence'} onClick={() => setTab('confidence')}>
                <LayersOutlined/> Confidence · {itemCount} facts
            </ButtonBase>
            <ButtonBase className={"db-op-tab" + (tab === 'actions' ? ' active' : '')} role="tab"
                        aria-selected={tab === 'actions'} onClick={() => setTab('actions')}>
                <ControlPointDuplicateOutlined/> Panels & actions
            </ButtonBase>
        </div>

        {tab === 'overview' && detail && <>
            <section className="db-op-card">
                <div className="db-op-card-head">
                    <span className="db-op-card-title">Why now</span>
                    <span className="db-op-card-aside">{opportunity.opportunity_type_label} · {computed.timingLabel}</span>
                </div>
                <ul className="db-op-bullets">
                    {detail.why_now.map((line, i) =>
                        <li key={line}>
                            <span className="db-op-bullet-text">{line}</span>
                            {renderWhyNowRef(detail.why_now_refs?.[i], i)}
                        </li>
                    )}
                </ul>
                {weakestGap &&
                    <div className="db-op-band">
                        <TaskAltOutlined/> Needs attention — {dimensionLabel(weakest)}:{' '}
                        {weakestGap.note ?? `missing — ${weakestGap.label.toLowerCase()}`}
                    </div>
                }
                {opportunity.related_impact && !opportunity.related_impact.additive &&
                    <div className="db-op-band">
                        <InfoOutlined/> {opportunity.related_impact.display_line}
                    </div>
                }
            </section>

            <div className="db-op-grid2">
                <section className="db-op-card">
                    <div className="db-op-card-head">
                        <span className="db-op-card-title">Potential impact</span>
                        <span className="db-op-card-aside">{valueType?.label} — {valueType?.unit}</span>
                    </div>
                    {valueRows.map(row =>
                        <div key={row.label} className="db-impact-row">
                            <span className="db-impact-label">
                                {row.label}
                                {row.sub && <span className="db-impact-sub">{row.sub}</span>}
                            </span>
                            <span className="db-impact-value">
                                {row.value}
                                {row.assumptionId
                                    ? <Tooltip title="Assumption — override requires a reason">
                                        <IconButton size="small" className="db-op-src" aria-label={`Edit ${row.label}`}
                                                    onClick={() => setEditingAssumption(row.assumptionId!)}>
                                            <EditOutlined/>
                                        </IconButton>
                                    </Tooltip>
                                    : <Tooltip title={`Source: ${dimensionLabel(row.cls)}`}>
                                        <IconButton size="small" className="db-op-src" aria-label={`${row.label} sources`}
                                                    onClick={e => setDimensionAnchor({cls: row.cls, el: e.currentTarget})}>
                                            <DescriptionOutlined/>
                                        </IconButton>
                                    </Tooltip>
                                }
                            </span>
                        </div>
                    )}
                    <div className="db-impact-row db-impact-total">
                        <span className="db-impact-label">Potential impact</span>
                        <span className="db-impact-value">{fmtFullEuro(computed.impact)}</span>
                    </div>
                </section>

                <section className="db-op-card" ref={timingCardRef}>
                    <div className="db-op-card-head">
                        <span className="db-op-card-title">Timing</span>
                        <span className="db-op-card-aside">Cost of waiting {computed.costDisplay}</span>
                    </div>
                    {computed.derivationSteps.map(step =>
                        <div key={step.step} className="db-timing-row">
                            <span className="db-timing-num">{step.step}</span>
                            <span className="db-timing-body">
                                {step.label}
                                {(step.source || step.note) && <span className="db-impact-sub">{step.source ?? step.note}</span>}
                            </span>
                            <span className={"db-timing-value" + (step.step === computed.derivationSteps.length ? ' final' : '')}>
                                {step.value}
                                {step.editable && step.assumption_ref &&
                                    <Tooltip title="Assumption — override requires a reason">
                                        <IconButton size="small" className="db-op-src" aria-label={`Edit ${step.label}`}
                                                    onClick={() => setEditingAssumption(step.assumption_ref!)}>
                                            <EditOutlined/>
                                        </IconButton>
                                    </Tooltip>
                                }
                            </span>
                        </div>
                    )}
                    <div className="db-op-band">
                        <InfoOutlined/> {opportunity.cost_of_waiting.plain_language}{opportunity.cost_of_waiting.note ? ` ${opportunity.cost_of_waiting.note}` : ''}
                    </div>
                </section>
            </div>

            {opportunity.assumptions && opportunity.assumptions.length > 0 &&
                <section className="db-op-card">
                    <div className="db-op-card-head">
                        <span className="db-op-card-title">Assumptions</span>
                        <span className="db-op-card-aside">Every override requires a reason</span>
                    </div>
                    <table className="db-assumption-table">
                        <thead>
                            <tr>
                                <th>Assumption</th>
                                <th>Value</th>
                                <th>Source</th>
                                <th>State</th>
                                <th>Propagates to</th>
                            </tr>
                        </thead>
                        <tbody>
                            {opportunity.assumptions.map(a => {
                                const override = state.assumptionOverrides[a.id];
                                return (
                                    <tr key={a.id}>
                                        <td>{a.label}</td>
                                        <td className="db-assumption-value">
                                            <b>{formatAssumptionValue(a.id, override?.value ?? a.value)}</b>
                                            <Tooltip title="Override — requires a reason">
                                                <IconButton size="small" className="db-op-src" aria-label={`Edit ${a.label}`}
                                                            onClick={() => setEditingAssumption(a.id)}>
                                                    <EditOutlined/>
                                                </IconButton>
                                            </Tooltip>
                                            {override &&
                                                <span className="db-impact-sub">was {formatAssumptionValue(a.id, a.value)} — “{override.reason}”</span>
                                            }
                                        </td>
                                        <td>{a.source}</td>
                                        <td>
                                            <span className="db-assumption-state">
                                                {override ? 'human override' : a.confidence_state.replace(/_/g, ' ')}
                                            </span>
                                        </td>
                                        <td>{a.propagates_to}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </section>
            }

            <div className="db-op-grid2">
                {opportunity.decision_owners &&
                    <section className="db-op-card">
                        <div className="db-op-card-head">
                            <span className="db-op-card-title">Decision owners</span>
                            <span className="db-op-card-aside">Inferred owners never block readiness</span>
                        </div>
                        {opportunity.decision_owners.map(s =>
                            <div key={s.name} className="db-owner-row">
                                <span className="db-owner-avatar">{s.name.split(' ').map(w => w[0]).join('').slice(0, 2)}</span>
                                <span className="db-owner-body">
                                    <span className="db-owner-name">
                                        {s.name}
                                        {s.assignment === 'inferred' &&
                                            <Tooltip title="Inferred from category ownership — confirm when you progress this opportunity">
                                                <span className="db-owner-inferred">inferred</span>
                                            </Tooltip>
                                        }
                                    </span>
                                    <span className="db-impact-sub">{s.role} · {s.why}</span>
                                </span>
                                {s.decision_right === 'Approve'
                                    ? <span className="db-owner-right approve">{s.decision_right}</span>
                                    : <span className="db-owner-right">{s.decision_right}</span>
                                }
                            </div>
                        )}
                    </section>
                }
                <section className="db-op-card">
                    <div className="db-op-card-head">
                        <span className="db-op-card-title">Goal fit & change log</span>
                        <span className="db-op-card-aside">Since yesterday</span>
                    </div>
                    {(opportunity.goal_fit ?? []).map(g =>
                        <div key={g.goal} className="db-gf-row">
                            <TrackChangesOutlined className="db-gf-icon"/>
                            <span className="db-owner-body">
                                <span className="db-owner-name">{g.goal}</span>
                                <span className="db-impact-sub">{g.period} · {g.fit}{g.note ? ` — ${g.note}` : ''}</span>
                            </span>
                        </div>
                    )}
                    <div className="db-op-band">
                        <ScheduleOutlined/> {detail.what_changed_since_yesterday}
                    </div>
                </section>
            </div>
        </>}

        {tab === 'confidence' && <>
            <section className="db-op-card">
                <div className="db-op-card-head">
                    <span className="db-op-card-title">Confidence — the four dimensions, weakest sets the opportunity</span>
                    <span className="db-op-card-aside">
                        {confidenceState(computed.confidence)} · {confidenceDisplay(computed)} · {readyToActLabel(computed)}
                    </span>
                </div>
                <WorklistOffer computed={computed}/>
                {CONFIDENCE_DIMENSIONS.map(cls =>
                    <DimensionSection key={cls} computed={computed} cls={cls} returnPath={returnPath}/>
                )}
            </section>
        </>}

        {tab === 'actions' && detail && <>
            <section className="db-op-card">
                <div className="db-op-card-head">
                    <span className="db-op-card-title">In the full view</span>
                </div>
                <div className="db-panel-panels">
                    {detail.panels.map(p => <Chip key={p} size="small" variant="outlined" label={p}/>)}
                </div>
            </section>
            <section className="db-op-card db-op-commit">
                {isConverted
                    ? <div className="db-initiative-line">
                        <CheckCircleOutline sx={{fontSize: 15}}/>
                        Now a tracked initiative · {formatCompactEuro(computed.impact)} {valueType?.label.toLowerCase()}
                    </div>
                    : <>
                        <div>
                            <Button
                                variant="contained"
                                size="small"
                                disableElevation
                                onClick={() => {
                                    dispatch({type: 'convert', opportunityId: opportunity.id});
                                    navigate(briefingPath);
                                }}
                            >
                                {detail.conversion_action.label}
                            </Button>
                            <div className="db-panel-effect">{detail.conversion_action.effect}</div>
                        </div>
                        <Button variant="text" size="small" className="db-card-dismiss" onClick={() => setDismissOpen(true)}>
                            {opportunity.actions.dismiss.label}
                        </Button>
                    </>
                }
            </section>
        </>}

        {editingAssumptionDef &&
            <AssumptionEditor assumption={editingAssumptionDef} open onClose={() => setEditingAssumption(null)}/>
        }

        <Popover
            open={Boolean(sourceAnchor)}
            anchorEl={sourceAnchor?.el}
            onClose={() => setSourceAnchor(null)}
            anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
            slotProps={{paper: {className: 'db-pop'}}}
        >
            {sourceAnchor &&
                <FactRow computed={computed} cls={sourceAnchor.cls} item={sourceAnchor.item} returnPath={returnPath}/>
            }
        </Popover>

        <Popover
            open={Boolean(dimensionAnchor)}
            anchorEl={dimensionAnchor?.el}
            onClose={() => setDimensionAnchor(null)}
            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            transformOrigin={{vertical: 'top', horizontal: 'right'}}
            slotProps={{paper: {className: 'db-pop'}}}
        >
            {dimensionAnchor && <>
                <div className="db-pop-title">
                    {dimensionLabel(dimensionAnchor.cls)} — the source of this number
                    <span className="db-pop-subtitle">
                        {BRIEFING.confidence_model.dimensions.find(c => c.id === dimensionAnchor.cls)?.question}
                    </span>
                </div>
                {opportunity.confidence[dimensionAnchor.cls].facts.map(item =>
                    <FactRow key={item.record_id} computed={computed} cls={dimensionAnchor.cls} item={item} returnPath={returnPath}/>
                )}
            </>}
        </Popover>

        <DismissDialog opportunity={opportunity} open={dismissOpen} onClose={() => setDismissOpen(false)} onDismissed={() => navigate(briefingPath)}/>
    </MainPageFacade>;
});

export const OpportunityDetailPage: React.FC = () => (
    <BriefingProvider>
        <OpportunityDetailContent/>
    </BriefingProvider>
);
